import { useForm, useFieldArray, Controller, type Resolver } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'

import { Button, TextInput } from '@atoms'
import { SelectField } from '@components/molecules/SelectField'
import { FormDialog } from '@components/molecules/FormDialog'
import Icon from '@components/utils/Icon'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'

import { useShiftAuthoring } from './ShiftAuthoringContext'
import { useAddShiftsMutation } from './useAddShiftsMutation'
import { shiftFieldsSchema } from './shiftFieldsSchema'
import { dayOptionsFor, type RoleOption } from './options'
import { RoleField } from './RoleField'

const t = scopedTranslation('author_shifts')
const tShift = scopedTranslation('attributes.shift')

type Row = { role: RoleOption | null; startTime: string; endTime: string; volunteersRequired: number }

const blankRow = (): Row => ({ role: null, startTime: '09:00', endTime: '12:00', volunteersRequired: 1 })

const validationSchema = yup.object({
  date: yup.string().required(),
  shifts: yup.array().of(yup.object(shiftFieldsSchema())).min(1).required(),
})

type FormValues = { date: string; shifts: Row[] }

type AddShiftsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  presetDate: string | null
}

export function AddShiftsDialog({ open, onOpenChange, presetDate }: AddShiftsDialogProps) {
  const { eventId, dates, roles, timeZone, onChange } = useShiftAuthoring()
  const { addShifts, isPending } = useAddShiftsMutation()
  const dayOptions = dayOptionsFor(dates, timeZone)

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema) as Resolver<FormValues>,
    mode: 'onTouched',
    defaultValues: { date: presetDate ?? dates[0], shifts: [blankRow()] },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'shifts' })

  const onSubmit = handleSubmit((values) => {
    const shifts = values.shifts.map((row) => ({
      roleName: (row.role?.value ?? '').trim(),
      startTime: timeFormatter.combineDateAndTime(values.date, row.startTime, timeZone),
      endTime: timeFormatter.combineDateAndTime(values.date, row.endTime, timeZone),
      volunteersRequired: row.volunteersRequired,
      draft: true,
    }))
    addShifts({
      eventId,
      shifts,
      onSuccess: () => {
        onOpenChange(false)
        onChange()
      },
    })
  })

  return (
    <FormDialog.Root open={open} onOpenChange={onOpenChange} size="2xl" testId="add-shifts-dialog">
      <FormDialog.Title errorsPath="rosterAssignment">{t('add_title')}</FormDialog.Title>
      <FormDialog.Form onSubmit={onSubmit}>
        <FormDialog.Body>
          {presetDate ? (
            <p className="tw:text-sm tw:text-gray-600">
              {t('add_to_day_prefix')}{' '}
              <span className="tw:font-semibold tw:text-gray-900">
                {timeFormatter.fromISO(presetDate, timeZone).toLongDate()}
              </span>
            </p>
          ) : (
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <SelectField
                  name={field.name}
                  value={field.value}
                  onValueChange={field.onChange}
                  label={t('day_label')}
                  options={dayOptions}
                  errors={errors}
                />
              )}
            />
          )}

          {fields.map((field, index) => (
            <div
              key={field.id}
              className="tw:flex tw:flex-wrap tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-gray-200 tw:p-3"
            >
              <div className="tw:w-28">
                <TextInput
                  type="time"
                  label={tShift('start_time')}
                  errors={errors}
                  {...register(`shifts.${index}.startTime`)}
                />
              </div>
              <div className="tw:w-28">
                <TextInput
                  type="time"
                  label={tShift('end_time')}
                  errors={errors}
                  {...register(`shifts.${index}.endTime`)}
                />
              </div>
              <div className="tw:min-w-48 tw:flex-1">
                <RoleField control={control} name={`shifts.${index}.role`} roles={roles} errors={errors} />
              </div>
              <div className="tw:w-24">
                <TextInput
                  type="number"
                  min={1}
                  label={tShift('volunteers')}
                  errors={errors}
                  {...register(`shifts.${index}.volunteersRequired`, { valueAsNumber: true })}
                />
              </div>
              {fields.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  aria-label={t('remove_row', { index: index + 1 })}
                  className="tw:mt-7 tw:flex tw:h-9 tw:w-9 tw:items-center tw:justify-center tw:rounded-lg tw:text-gray-500 tw:hover:bg-error-50 tw:hover:text-error-600 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-error-400"
                >
                  <Icon type="trash-01" className="tw:h-4 tw:w-4" aria-hidden />
                </button>
              )}
            </div>
          ))}

          <Button type="button" rank="link" leadingIcon="plus" onClick={() => append(blankRow())}>
            {t('add_another')}
          </Button>
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('add_submit')} isLoading={isPending} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
